// @file Vue composable that creates and manages a Tiptap editor instance for whiteboard text shapes
// This editor is used to handle rich text content in whiteboard text objects

import { dir } from '@@/bits/current_dir'
import type { Content, Editor as TiptapEditor } from '@padlet/whiteboard-tiptap'
import { createTiptapEditor } from '@padlet/whiteboard-tiptap'

/**
 * Creates a headless Tiptap editor instance for manipulating rich text content in whiteboard text shapes.
 * It's used to manipulate text content at the shape level, like applying formatting to entire text blocks.
 */
const useWhiteboardHeadlessTiptapEditor = (
  content: Content,
): {
  whiteboardTiptapEditor: TiptapEditor
  setBold: () => Content
  unsetBold: () => Content
  setItalic: () => Content
  unsetItalic: () => Content
  setStrike: () => Content
  unsetStrike: () => Content
  toggleBulletList: () => Content
  toggleOrderedList: () => Content
  unsetColor: () => Content
} => {
  const whiteboardTiptapEditor = createTiptapEditor({
    content: '',
    context: { direction: dir() },
  })

  whiteboardTiptapEditor.commands.setContent(content)

  function applyTextEditingOperation(operation: () => void): Content {
    whiteboardTiptapEditor.commands.setTextSelection({
      from: 0,
      to: whiteboardTiptapEditor.state.doc.content.size,
    })

    operation()

    return whiteboardTiptapEditor.getJSON()
  }

  function setBold(): Content {
    return applyTextEditingOperation(() => whiteboardTiptapEditor.commands.setBold())
  }

  function unsetBold(): Content {
    return applyTextEditingOperation(() => whiteboardTiptapEditor.commands.unsetBold())
  }

  function setItalic(): Content {
    return applyTextEditingOperation(() => whiteboardTiptapEditor.commands.setItalic())
  }

  function unsetItalic(): Content {
    return applyTextEditingOperation(() => whiteboardTiptapEditor.commands.unsetItalic())
  }

  function setStrike(): Content {
    return applyTextEditingOperation(() => whiteboardTiptapEditor.commands.setStrike())
  }

  function unsetStrike(): Content {
    return applyTextEditingOperation(() => whiteboardTiptapEditor.commands.unsetStrike())
  }

  function toggleBulletList(): Content {
    return applyTextEditingOperation(() => whiteboardTiptapEditor.commands.toggleBulletList())
  }

  function toggleOrderedList(): Content {
    return applyTextEditingOperation(() => whiteboardTiptapEditor.commands.toggleOrderedList())
  }

  function unsetColor(): Content {
    return applyTextEditingOperation(() => whiteboardTiptapEditor.commands.unsetColor())
  }

  return {
    whiteboardTiptapEditor,
    setBold,
    unsetBold,
    setItalic,
    unsetItalic,
    setStrike,
    unsetStrike,
    toggleBulletList,
    toggleOrderedList,
    unsetColor,
  }
}

export { useWhiteboardHeadlessTiptapEditor }
